import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, MapPin } from "lucide-react";

/**
 * Mobile-first autocomplete that replaces <datalist> (which is broken on
 * iOS Safari + Android Chrome). Shows a proper dropdown on focus/tap,
 * filters as the user types, and selects via onMouseDown (fires before
 * input blur, so the tap always registers on touch devices too).
 */
export default function LocationAutocomplete({
  value,
  onChange,
  options,
  placeholder = "Start typing…",
  testId,
  ariaLabel,
  icon = true,
  maxResults = 60,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    const needle = (value || "").trim().toLowerCase();
    if (!needle) return options.slice(0, maxResults);
    const starts = [];
    const contains = [];
    for (const o of options) {
      const lo = o.toLowerCase();
      if (lo.startsWith(needle)) starts.push(o);
      else if (lo.includes(needle)) contains.push(o);
      if (starts.length + contains.length >= maxResults) break;
    }
    return [...starts, ...contains];
  }, [value, options, maxResults]);

  // Close when tapping/clicking outside
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const pick = (v) => {
    onChange({ target: { value: v } });
    setOpen(false);
    // blur so mobile keyboard collapses
    inputRef.current?.blur();
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        {icon && (
          <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-sa-copper" />
        )}
        <input
          ref={inputRef}
          value={value || ""}
          onChange={(e) => {
            onChange(e);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          data-testid={testId}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          className={`w-full rounded-2xl border border-white bg-white ${
            icon ? "pl-11" : "pl-5"
          } pr-11 py-4 text-sa-forest outline-none transition-colors focus:border-sa-copper`}
        />
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen((v) => !v);
            inputRef.current?.focus();
          }}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-sa-muted hover:text-sa-forest"
          aria-label="Toggle options"
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {open && (
        <ul
          role="listbox"
          data-testid={testId ? `${testId}-list` : undefined}
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-64 overflow-y-auto overscroll-contain rounded-2xl border border-sa-beige bg-white py-1.5 shadow-[0_20px_50px_-16px_rgba(10,46,34,0.25)]"
        >
          {filtered.length === 0 ? (
            <li className="px-4 py-3 text-sm text-sa-muted">No matches. You can also type freely.</li>
          ) : (
            filtered.map((opt) => (
              <li
                key={opt}
                role="option"
                data-testid={testId ? `${testId}-option-${opt.toLowerCase().replace(/\s+/g, "-")}` : undefined}
                // onMouseDown fires BEFORE input blur → works on touch + mouse
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(opt);
                }}
                className="cursor-pointer select-none px-4 py-2.5 text-sm text-sa-forest transition-colors hover:bg-sa-beige/70"
              >
                {opt}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
